import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { fmtPHP, fmtDate } from "@/lib/api";
import { PCard, StatusPill } from "@/components/portal/PortalBits";
import { ChevronRight, KeyRound } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BalanceCard, DashboardActions, ApprovalTipsBanner } from "@/components/portal/DashboardWidgets";

function LoanRow({ l }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/portal/loans/${l.id}`)} data-testid={`portal-loan-${l.id}`}
      className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 text-left">
      <div>
        <div className="font-semibold text-slate-900 text-sm">{fmtPHP(l.principal)} · {l.term_months} mo</div>
        <div className="flex items-center gap-2 mt-1">
          <StatusPill status={l.status} />
          {l.otp_required && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
              <KeyRound className="h-3 w-3" /> Code needed
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
}

export default function PortalHome() {
  const { data: d, openPanel } = useOutletContext();
  if (!d) return <div className="text-center text-slate-400 py-20" data-testid="portal-loading">Loading your account…</div>;
  const b = d.borrower;
  const runningLoan = d.loans.find((l) => l.id === d.next_due?.loan_id) || d.loans.find((l) => ["Approved", "Disbursed", "Active", "Overdue"].includes(l.status));
  const tabClass = "flex-1 rounded-none border-b-2 border-transparent bg-transparent py-3 text-xs text-slate-400 shadow-none data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:text-[#00A8DE] data-[state=active]:border-[#00A8DE] transition-colors";
  return (
    <div data-testid="portal-home">
      <Tabs defaultValue="credit">
        <TabsList className="flex w-full h-auto rounded-none bg-white p-0 border-b border-slate-100 mb-5" aria-label="Dashboard tabs">
          <TabsTrigger value="installment" data-testid="dashboard-installment-tab" className={tabClass}>Installment Payment</TabsTrigger>
          <TabsTrigger value="credit" data-testid="dashboard-credit-tab" className={tabClass}>Credit Score</TabsTrigger>
        </TabsList>
        <TabsContent value="credit" className="mt-0" data-testid="dashboard-credit-panel">
          <BalanceCard borrower={b} openPanel={openPanel} />
          <p data-testid="dashboard-credit-disclaimer" className="text-[11px] leading-relaxed text-slate-500 mt-2 px-1">{b.credit_score_is_default ? "Starting score · unverified" : "Internal recorded score"} · +0.5% is a display reference only</p>
        </TabsContent>
        <TabsContent value="installment" className="mt-0 space-y-3" data-testid="dashboard-installment-panel">
          <div className="grid grid-cols-2 gap-3"><PCard><p className="text-[10px] text-slate-500">Outstanding</p><p data-testid="portal-outstanding" className="text-base font-bold text-slate-900 mt-1">{fmtPHP(d.total_outstanding)}</p></PCard><PCard><p className="text-[10px] text-slate-500">Next due</p><p data-testid="portal-next-due" className="text-base font-bold text-slate-900 mt-1">{d.next_due ? fmtPHP(d.next_due.amount_due - d.next_due.amount_paid) : "No dues"}</p><p className="text-[10px] text-slate-400">{d.next_due ? fmtDate(d.next_due.due_date) : "No active schedule"}</p></PCard></div>
          {runningLoan && <PCard data-testid="portal-repayment-plan"><div className="flex justify-between text-xs text-slate-600"><span>Monthly installment</span><strong>{fmtPHP(runningLoan.monthly_payment)}</strong></div><Link to={`/portal/loans/${runningLoan.id}`} data-testid="portal-view-schedule" className="block text-center text-xs font-semibold text-[#009DCD] mt-3">View full schedule</Link></PCard>}
          <PCard className="p-1">{!d.loans.length ? <p data-testid="dashboard-no-loans" className="text-xs text-slate-500 text-center py-5">No loans yet. Apply when you’re ready.</p> : d.loans.map((l) => <LoanRow key={l.id} l={l} />)}</PCard>
          <Link to="/portal/history" data-testid="portal-see-all-history" className="block text-right text-xs font-semibold text-[#009DCD]">View payment records →</Link>
        </TabsContent>
      </Tabs>
      <DashboardActions openPanel={openPanel} />
      <ApprovalTipsBanner onClick={() => openPanel("tips")} />
    </div>
  );
}
